import React, { useState, useEffect } from 'react';
import { IonContent, useIonToast, useIonViewDidEnter } from '@ionic/react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import Header from '../components/Header/Header';
import Footer from '../components/Footer';
import { onScroll } from '../services/sticky-menu.service';
import { useAppContext } from '../services/app-context.service';
import { appSubDir } from '../configs/app';
import { createShippingBooking } from '../services/shipping-booking.service';
import { ShippingBooking, ShippingBookingItem } from '../types/shipping-booking.type';
import './CreateBooking.scss';

const CreateBooking: React.FC = () => {
    const history = useHistory();
    const { user, settings } = useAppContext();
    const [presentToast] = useIonToast();

    // Form fields
    const [method, setMethod] = useState<'Air Shipment' | 'Sea Shipment'>('Air Shipment');
    const [shippingMark, setShippingMark] = useState('');
    const [trackingNo, setTrackingNo] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [cartonCount, setCartonCount] = useState<number>(1);
    const [weight, setWeight] = useState<number>(settings?.shipping_service_min_kg ?? 5);
    const [packagingSize, setPackagingSize] = useState('');
    const [shippingRoute, setShippingRoute] = useState('Guangzhou');
    const [customerNotes, setCustomerNotes] = useState('');

    // Items list
    const [items, setItems] = useState<ShippingBookingItem[]>([
        { item_name: '', quantity: 1, is_special_category: false }
    ]);

    // Terms agreement
    const [agreeTerms, setAgreeTerms] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createdBooking, setCreatedBooking] = useState<ShippingBooking | null>(null);

    // Prefill logged-in user's data
    useEffect(() => {
        if (user && user.token) {
            if (user.name && !customerName) {
                setCustomerName(user.name);
            }
            if (user.name && !shippingMark) {
                setShippingMark(user.name);
            }
            if (user.phone && !customerPhone) {
                setCustomerPhone(user.phone);
            }
        }
    }, [user]);

    useIonViewDidEnter(() => {
        const tabBar = document.querySelector('ion-tab-bar');
        if (tabBar !== null) {
            tabBar.style.display = 'none';
        }
    });

    // Warehouse addresses from settings or defaults
    const warehouseAir = settings?.shipping_service_air_warehouse || 
`广州市白云区北湖北街十社停车场A1仓（导航至联滘路与北湖北路交叉口）
收件人沈小姐
电话17665759512`;

    const warehouseSea = settings?.shipping_service_sea_warehouse || 
`广州市白云区北湖北街十社停车场A2仓（导航至联滘路与北湖北路交叉口）
收件人沈小姐
电话17665759512`;

    const minWeight = settings?.shipping_service_min_kg ?? 5;

    // Handle Item changes
    const handleItemChange = (index: number, field: keyof ShippingBookingItem, value: any) => {
        const updated = [...items];
        updated[index] = {
            ...updated[index],
            [field]: value
        };
        setItems(updated);
    };

    const addItem = () => {
        setItems([
            ...items,
            { item_name: '', quantity: 1, is_special_category: false }
        ]);
    };

    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
            presentToast({
                message: `${label} copied to clipboard!`,
                duration: 2000,
                color: 'success'
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!shippingMark.trim()) {
            presentToast({ message: 'Shipping Mark is required', duration: 2500, color: 'danger' });
            return;
        }

        if (!trackingNo.trim()) {
            presentToast({ message: 'Tracking Number is mandatory', duration: 2500, color: 'danger' });
            return;
        }

        if (!customerName.trim()) {
            presentToast({ message: 'Customer Name is required', duration: 2500, color: 'danger' });
            return;
        }

        if (!customerPhone.trim()) {
            presentToast({ message: 'Customer Phone is required', duration: 2500, color: 'danger' });
            return;
        }

        const validItems = items.filter(it => it.item_name.trim().length > 0);
        if (validItems.length === 0) {
            presentToast({ message: 'Please provide at least one item name', duration: 2500, color: 'danger' });
            return;
        }

        if (!agreeTerms) {
            presentToast({ message: 'Please accept Terms & Conditions before placing booking', duration: 2500, color: 'warning' });
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await createShippingBooking({
                method,
                shipping_mark: shippingMark.trim(),
                tracking_no: trackingNo.trim(),
                customer_name: customerName.trim(),
                customer_phone: customerPhone.trim(),
                customer_address: customerAddress.trim(),
                carton_count: cartonCount,
                weight: Number(weight) || minWeight,
                packaging_size: packagingSize.trim(),
                shipping_route: shippingRoute,
                items: validItems,
                customer_notes: customerNotes.trim()
            });

            setCreatedBooking(res.booking);
            presentToast({
                message: 'Booking placed successfully!',
                duration: 3000,
                color: 'success'
            });
        } catch (err: any) {
            console.error('Create booking failed:', err);
            const msg = err.response?.data?.message || err.response?.data?.errors?.tracking_no?.[0] || 'Failed to place booking. Please try again.';
            presentToast({
                message: msg,
                duration: 3500,
                color: 'danger'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <IonContent fullscreen scrollEvents={true} onIonScroll={onScroll}>
            <Header />

            <div className="create-booking-page">
                {/* Top Nav Bar */}
                <div className="booking-top-nav" style={{ justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button className="back-btn" onClick={() => history.push(appSubDir)} aria-label="Go Back">
                            &laquo;
                        </button>
                        <h1>Create Booking</h1>
                    </div>
                    <Link
                        to={appSubDir + "my-bookings"}
                        style={{
                            fontSize: '0.86rem',
                            fontWeight: 700,
                            color: '#0a6670',
                            textDecoration: 'none',
                            padding: '6px 12px',
                            background: '#f0fdfa',
                            border: '1px solid #ccfbf1',
                            borderRadius: 6
                        }}
                    >
                        Track Status / My Bookings &raquo;
                    </Link>
                </div>

                <div className="booking-content-container">
                    {createdBooking ? (
                        /* Placed Booking Success Card */
                        <div className="booking-success-box">
                            <div className="success-icon">&#10003;</div>
                            <h2>Booking Placed Successfully!</h2>
                            <p style={{ color: '#475569', margin: '4px 0 12px 0' }}>
                                Your shipping service booking has been recorded. You will receive SMS updates on your mobile.
                            </p>
                            <div className="booking-id-tag">
                                Booking ID: {createdBooking.booking_id}
                            </div>

                            <div className="details-summary">
                                <div className="row-item">
                                    <strong>Shipping Mark:</strong>
                                    <span>{createdBooking.shipping_mark}</span>
                                </div>
                                <div className="row-item">
                                    <strong>Tracking Number:</strong>
                                    <span>{createdBooking.tracking_no}</span>
                                </div>
                                <div className="row-item">
                                    <strong>Method:</strong>
                                    <span>{createdBooking.method}</span>
                                </div>
                                <div className="row-item">
                                    <strong>Total Cartons:</strong>
                                    <span>{createdBooking.carton_count}</span>
                                </div>
                                <div className="row-item">
                                    <strong>Shipping Route:</strong>
                                    <span>{createdBooking.shipping_route}</span>
                                </div>
                                <div className="row-item">
                                    <strong>Status:</strong>
                                    <span style={{ color: '#0d9488' }}>{createdBooking.status}</span>
                                </div>
                            </div>

                            <div className="action-buttons">
                                <Link
                                    to={`${appSubDir}my-bookings?track=${createdBooking.booking_id}`}
                                    className="btn-primary"
                                    style={{ textDecoration: 'none' }}
                                >
                                    Track Live Status &amp; Timeline &raquo;
                                </Link>
                                <button
                                    type="button"
                                    className="btn-outline"
                                    onClick={() => {
                                        setCreatedBooking(null);
                                        setTrackingNo('');
                                        setItems([{ item_name: '', quantity: 1, is_special_category: false }]);
                                    }}
                                >
                                    + Create Another Booking
                                </button>
                                <Link to={appSubDir} className="btn-outline">
                                    Go to Home
                                </Link>
                            </div>
                        </div>
                    ) : (
                        /* Booking Form */
                        <form onSubmit={handleSubmit}>
                            {/* Card 1: Booking Info */}
                            <div className="booking-card">
                                <h2 className="card-header-title">Booking Info</h2>

                                <div className="form-group">
                                    <label className="form-label">
                                        <span className="req">*</span> Method
                                    </label>
                                    <select
                                        className="form-control"
                                        value={method}
                                        onChange={(e) => setMethod(e.target.value as any)}
                                    >
                                        <option value="Air Shipment">Air</option>
                                        <option value="Sea Shipment">Sea</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        <span className="req">*</span> Shipping Mark
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter your shipping mark"
                                        value={shippingMark}
                                        onChange={(e) => setShippingMark(e.target.value)}
                                        required
                                    />
                                    {user.name && (
                                        <div className="helper-text">
                                            (Auto-prefilled from your account profile)
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        <span className="req">*</span> Tracking
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Tracking"
                                        value={trackingNo}
                                        onChange={(e) => setTrackingNo(e.target.value)}
                                        required
                                    />
                                    <div className="helper-text">Mandatory China domestic tracking number</div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        <span className="req">*</span> Customer Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Your full name"
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        <span className="req">*</span> Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        placeholder="e.g. 017xxxxxxxx"
                                        value={customerPhone}
                                        onChange={(e) => setCustomerPhone(e.target.value)}
                                        required
                                    />
                                    <div className="helper-text">Used to send tracking and movement SMS</div>
                                </div>
                            </div>

                            {/* Card 2: Carton */}
                            <div className="booking-card">
                                <h2 className="card-header-title">Carton</h2>

                                <div className="carton-counter-box">
                                    <button
                                        type="button"
                                        className="counter-btn"
                                        onClick={() => setCartonCount(Math.max(1, cartonCount - 1))}
                                        disabled={cartonCount <= 1}
                                    >
                                        &minus;
                                    </button>
                                    <input
                                        type="number"
                                        min="1"
                                        className="counter-display"
                                        value={cartonCount}
                                        onChange={(e) => setCartonCount(Math.max(1, parseInt(e.target.value) || 1))}
                                    />
                                    <button
                                        type="button"
                                        className="counter-btn"
                                        onClick={() => setCartonCount(cartonCount + 1)}
                                    >
                                        &#43;
                                    </button>
                                </div>
                                <div className="form-group">
                                    <div className="helper-text">How many cartons in this tracking number?*</div>
                                </div>
                            </div>

                            {/* Card 3: Item Details of Carton */}
                            <div className="booking-card">
                                <h2 className="card-header-title">Item Details Of Carton - 1</h2>

                                {items.map((item, idx) => (
                                    <div className="item-entry-card" key={idx}>
                                        <div className="item-entry-header">
                                            <span>Item #{idx + 1}</span>
                                            {items.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="remove-item-btn"
                                                    onClick={() => removeItem(idx)}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">
                                                <span className="req">*</span> Item Name
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter item name"
                                                value={item.item_name}
                                                onChange={(e) => handleItemChange(idx, 'item_name', e.target.value)}
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">
                                                <span className="req">*</span> Total Quantity
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                className="form-control"
                                                placeholder="Total quantity"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(idx, 'quantity', parseInt(e.target.value) || 1)}
                                                required
                                            />
                                        </div>

                                        <label className="checkbox-container">
                                            <input
                                                type="checkbox"
                                                checked={item.is_special_category}
                                                onChange={(e) => handleItemChange(idx, 'is_special_category', e.target.checked)}
                                            />
                                            <span>এই পণ্য কি ব্যাটারি, লিকুইড, কসমেটিকস বা পাউডার ক্যাটাগরি।</span>
                                        </label>
                                    </div>
                                ))}

                                <button type="button" className="add-more-btn" onClick={addItem}>
                                    <span className="plus-icon">&#43;</span> Add More
                                </button>
                            </div>

                            {/* Card: Weight & Packaging (From Handwritten note) */}
                            <div className="booking-card">
                                <h2 className="card-header-title">Weight &amp; Packaging Size</h2>

                                <div className="form-group">
                                    <label className="form-label">
                                        Weight (KG) <span style={{ color: '#0d9488', fontWeight: 600 }}>&bull; Minimum {minWeight} kg</span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        className="form-control"
                                        placeholder={`Minimum ${minWeight} kg`}
                                        value={weight}
                                        onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
                                    />
                                    <div className="helper-text">
                                        * ওজনের হিসাব: সর্বনিম্ন {minWeight} কেজি থেকে হিসাব হবে।
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Packaging Size
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g. 40x30x25 cm"
                                        value={packagingSize}
                                        onChange={(e) => setPackagingSize(e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Shipping Route / Time
                                    </label>
                                    <select
                                        className="form-control"
                                        value={shippingRoute}
                                        onChange={(e) => setShippingRoute(e.target.value)}
                                    >
                                        <option value="Guangzhou">Guangzhou (Gunzu)</option>
                                        <option value="Hong Kong">Hong Kong (Honkong)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Card 4: Summary & Warehouse Addresses */}
                            <div className="booking-card">
                                <h2 className="card-header-title">Summary</h2>

                                <div className="summary-carton-row">
                                    <span>Total cartons</span>
                                    <span className="badge-carton">{cartonCount}</span>
                                </div>

                                {/* Warehouse Air */}
                                <div className="warehouse-box">
                                    <div className="warehouse-title">
                                        <span>WAREHOUSE ADDRESS (By Air)</span>
                                        <button
                                            type="button"
                                            className="copy-badge"
                                            onClick={() => copyToClipboard(warehouseAir, 'Air Warehouse Address')}
                                        >
                                            Copy
                                        </button>
                                    </div>
                                    <pre className="warehouse-text">{warehouseAir}</pre>
                                </div>

                                {/* Warehouse Sea */}
                                <div className="warehouse-box">
                                    <div className="warehouse-title">
                                        <span>WAREHOUSE ADDRESS (By Sea)</span>
                                        <button
                                            type="button"
                                            className="copy-badge"
                                            onClick={() => copyToClipboard(warehouseSea, 'Sea Warehouse Address')}
                                        >
                                            Copy
                                        </button>
                                    </div>
                                    <pre className="warehouse-text">{warehouseSea}</pre>
                                </div>

                                <label className="terms-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={agreeTerms}
                                        onChange={(e) => setAgreeTerms(e.target.checked)}
                                    />
                                    <span>
                                        I have read and agreed to the <Link to={appSubDir + "terms-and-conditions"} target="_blank">Terms &amp; Conditions</Link> before sending my goods to warehouse.
                                    </span>
                                </label>

                                <button
                                    type="submit"
                                    className="place-booking-btn"
                                    disabled={isSubmitting || !agreeTerms}
                                >
                                    {isSubmitting ? 'Placing Booking...' : 'Place Booking'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            <Footer />
        </IonContent>
    );
};

export default CreateBooking;

